import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Loader2, MapPin, Users } from 'lucide-react';
import { eventsApi } from '../services/eventsApi';
import { AuthPromptModal } from '../components/AuthPromptModal';
import { useAuthGate } from '../hooks/useAuthGate';

export default function LandingEventDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isAuthed,
    userId,
    requireAuth,
    authPromptOpen,
    closeAuthPrompt,
    confirmAuthPrompt,
  } = useAuthGate();

  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    return () => document.documentElement.classList.remove('landing-page');
  }, []);

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      const events = await eventsApi.getAll();
      return events.find((e) => e.id === id) ?? null;
    },
    enabled: !!id,
  });

  const { data: registrations = [] } = useQuery({
    queryKey: ['user-registrations', userId],
    queryFn: () => eventsApi.getUserRegistrations(userId!),
    enabled: isAuthed && !!userId,
  });

  const isRegistered = registrations.some((r) => r.eventId === id);

  const registerMutation = useMutation({
    mutationFn: () => eventsApi.register(userId!, id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  const unregisterMutation = useMutation({
    mutationFn: () => eventsApi.unregister(userId!, id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-registrations', userId] }),
  });

  const isPending =
    (registerMutation.isPending && !isRegistered) || (unregisterMutation.isPending && isRegistered);

  function handleRegisterClick() {
    requireAuth(`/events/${id}`, () => {
      if (isRegistered) unregisterMutation.mutate();
      else registerMutation.mutate();
    });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'rgba(255,255,255,0.35)' }} />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: '#0a0a0a', color: '#fff' }}>
        <p className="mb-6" style={{ color: 'rgba(255,255,255,0.45)' }}>Событие не найдено</p>
        <Link to="/events" className="underline text-sm">К афише</Link>
      </div>
    );
  }

  const dateObj = new Date(event.date);
  const formattedDate = dateObj.toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="landing-root min-h-screen text-white" style={{ background: '#0a0a0a' }}>
      <header className="flex items-center justify-between gap-4 px-5 sm:px-10 pt-6 pb-4">
        <button
          type="button"
          onClick={() => navigate('/events')}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase font-semibold"
          style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.7)' }}
        >
          <ArrowLeft size={16} /> Афиша
        </button>
        <Link
          to="/app/home"
          className="text-[12px] tracking-[0.18em] uppercase font-semibold"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          В приложение
        </Link>
      </header>

      <div className="max-w-3xl mx-auto">
        {event.image && (
          <div className="relative w-full" style={{ paddingTop: '52%' }}>
            <img src={event.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, rgba(10,10,10,0.2) 0%, transparent 40%, #0a0a0a 100%)',
              }}
            />
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-5 sm:px-10 pb-24 -mt-8 relative"
        >
          <h1
            className="text-3xl sm:text-4xl font-bold uppercase mb-6"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            {event.title}
          </h1>

          <div
            className="rounded-3xl p-5 sm:p-6 mb-6 space-y-4"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <MetaRow icon={<Calendar size={18} />} label="Дата" value={formattedDate} />
            {event.time && <MetaRow icon={<Clock size={18} />} label="Время" value={event.time} />}
            {event.location && <MetaRow icon={<MapPin size={18} />} label="Место" value={event.location} />}
            {event.registrationsCount > 0 && (
              <MetaRow
                icon={<Users size={18} />}
                label="Записались"
                value={String(event.registrationsCount)}
              />
            )}
          </div>

          {event.description && (
            <div
              className="rounded-2xl p-5 mb-8"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <p
                className="text-[11px] tracking-[0.2em] uppercase mb-3"
                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
              >
                Описание
              </p>
              <p
                className="text-base leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Manrope, sans-serif' }}
              >
                {event.description}
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={handleRegisterClick}
            disabled={isPending}
            className="w-full py-4 rounded-2xl font-semibold text-sm tracking-[0.1em] uppercase transition-all active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
            style={
              isRegistered
                ? {
                    background: 'transparent',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    fontFamily: 'Manrope, sans-serif',
                  }
                : { background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }
            }
          >
            {isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : isRegistered ? (
              'Отменить запись'
            ) : isAuthed ? (
              'Записаться'
            ) : (
              'Войти и записаться'
            )}
          </button>
        </motion.div>
      </div>

      <AuthPromptModal
        open={authPromptOpen}
        onClose={closeAuthPrompt}
        onConfirm={confirmAuthPrompt}
      />
    </div>
  );
}

function MetaRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>{icon}</span>
      <div>
        <p
          className="text-[11px] tracking-[0.16em] uppercase font-semibold"
          style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
        >
          {label}
        </p>
        <p className="text-sm font-medium mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {value}
        </p>
      </div>
    </div>
  );
}
